import { Component, Input, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { lastValueFrom, map } from 'rxjs';
import {
  GiftCycleControllerService,
  GiftCycleResponseDTO
} from 'src/app/admin-api';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

type GiftCycleStatus = 'ACTIVE' | 'SCHEDULED' | 'CLOSED';

@Component({
  selector: 'app-mgm-cycle-detail',
  templateUrl: './mgm-cycle-detail.component.html',
  styleUrl: './mgm-cycle-detail.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class MgmCycleDetailComponent implements OnInit {
  private giftCycleService = inject(GiftCycleControllerService);
  private title = inject(Title);
  private router = inject(Router);

  @Input() cycleId!: string;

  cycle?: GiftCycleResponseDTO;
  canEdit = false;
  status: GiftCycleStatus = 'CLOSED';
  statusLabel = 'Fechado';
  totalProducts = 0;
  totalRules = 0;

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Marketing - Detalhe do Ciclo');
    await this.loadCycle();
  }

  async loadCycle(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.cycle = await lastValueFrom(
        this.giftCycleService
          .getCycle(Number(this.cycleId))
          .pipe(map((r) => r.result))
      );
      this.status = this.derivedStatus(this.cycle?.referenceMonth);
      this.statusLabel = this.statusToLabel(this.status);
      this.canEdit = this.status !== 'CLOSED';
      this.updateTotals();
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  edit(): void {
    this.router.navigate(['/marketing/mgm', this.cycleId, 'edit']);
  }

  formatReferenceMonth(ref?: number): string {
    if (!ref) return '-';
    const s = String(ref);
    if (s.length !== 6) return s;
    return `${s.substring(4, 6)}/${s.substring(0, 4)}`;
  }

  private updateTotals(): void {
    const gifts = this.cycle?.gifts ?? [];
    this.totalProducts = gifts.reduce((sum, gift) => sum + (gift.products?.length ?? 0), 0);
    this.totalRules = gifts.reduce((sum, gift) => sum + (gift.giftRuleIds?.length ?? 0), 0);
  }

  private derivedStatus(referenceMonth?: number): GiftCycleStatus {
    if (!referenceMonth) return 'CLOSED';
    const current = this.currentReferenceMonth();
    if (referenceMonth < current) return 'CLOSED';
    if (referenceMonth === current) return 'ACTIVE';
    return 'SCHEDULED';
  }

  private statusToLabel(status: GiftCycleStatus): string {
    if (status === 'ACTIVE') return 'Ativo';
    if (status === 'SCHEDULED') return 'Agendado';
    return 'Fechado';
  }

  private currentReferenceMonth(): number {
    const now = new Date();
    return now.getFullYear() * 100 + (now.getMonth() + 1);
  }
}
